// src/components/PrintDialog.js
import React, { useRef, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Stack,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';

import PrintableExportSlip from './PrintableExportSlip';
import PrintableImportSlip from './PrintableImportSlip';

import PrintIcon from '@mui/icons-material/Print';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';     // Xuất Hợp đồng
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';     // Xuất ĐƠN HÀNG (PO)
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'; // Xuất Phiếu kiểm soát
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { companies, getCompany } from '../appConfig';

// Xuất Word
import { exportContractDocx as exportContract } from '../utils/exportSoToWord';
import { exportPODocxLikeSO } from '../utils/exportPOToWord';
import { exportKiemSoatDocx } from '../utils/exportKiemSoatToWord';

/* -------------------------------------------------------
   HỘP THOẠI CHỌN SẢN PHẨM (dùng chung cho 3 loại export)
------------------------------------------------------- */
function ProductPickerDialog({
  open,
  onClose,
  items = [],
  onConfirm,
  title = 'Chọn sản phẩm',
  confirmLabel = 'Xác nhận',
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set(items.map((_, i) => i))); // mặc định CHỌN HẾT

  useEffect(() => {
    setSelected(new Set(items.map((_, i) => i)));
    setQuery('');
  }, [items, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.map((it, i) => ({ ...it, _idx: i }));
    return items
      .map((it, i) => ({ ...it, _idx: i }))
      .filter(
        (r) =>
          String(r.productName || r.name || '').toLowerCase().includes(q) ||
          String(r.productSku || r.code || r.sku || '').toLowerCase().includes(q)
      );
  }, [items, query]);

  const allVisibleChecked = filtered.length > 0 && filtered.every((r) => selected.has(r._idx));
  const someVisibleChecked = filtered.some((r) => selected.has(r._idx)) && !allVisibleChecked;

  const toggleOne = (rowIdx) => {
    const s = new Set(selected);
    if (s.has(rowIdx)) s.delete(rowIdx);
    else s.add(rowIdx);
    setSelected(s);
  };

  const toggleAllVisible = () => {
    const s = new Set(selected);
    if (allVisibleChecked) filtered.forEach((r) => s.delete(r._idx));
    else filtered.forEach((r) => s.add(r._idx));
    setSelected(s);
  };

  const handleConfirm = () => {
    const out = items.filter((_, i) => selected.has(i));
    if (!out.length) return;
    onConfirm?.(out);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Tìm theo tên hoặc mã…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> }}
          />
          <Button
            onClick={toggleAllVisible}
            startIcon={
              allVisibleChecked ? <CheckBoxOutlinedIcon /> : <CheckBoxOutlineBlankOutlinedIcon />
            }
            variant="outlined"
          >
            {allVisibleChecked ? 'Bỏ chọn tất cả' : 'Chọn tất cả (đang hiển thị)'}
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 720 }}>
            <colgroup>
              <col style={{ width: 48 }} />
              <col style={{ width: 60 }} />
              <col style={{ width: '45%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someVisibleChecked}
                    checked={allVisibleChecked}
                    onChange={toggleAllVisible}
                  />
                </TableCell>
                <TableCell align="center">#</TableCell>
                <TableCell align="left">Tên sản phẩm</TableCell>
                <TableCell align="center">Mã SP</TableCell>
                <TableCell align="center">SL</TableCell>
                <TableCell align="right">Giá/Thành tiền</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => {
                const price =
                  Number(r.exportPrice ?? r.price ?? r.unitPrice ?? r.importPrice) || 0;
                const line = (Number(r.quantity) || 0) * price;
                return (
                  <TableRow
                    key={r._idx}
                    hover
                    onClick={() => toggleOne(r._idx)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(r._idx);
                      }}
                    >
                      <Checkbox checked={selected.has(r._idx)} />
                    </TableCell>
                    <TableCell align="center">{r._idx + 1}</TableCell>
                    <TableCell align="left">{r.productName || r.name}</TableCell>
                    <TableCell align="center">{r.productSku || r.code || r.sku}</TableCell>
                    <TableCell align="center">{r.quantity}</TableCell>
                    <TableCell align="right">
                      {Intl.NumberFormat('vi-VN').format(line)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Không có sản phẩm phù hợp
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Mẹo: lọc nhanh bằng ô tìm kiếm. “Chọn tất cả” chỉ áp dụng cho danh sách đang hiển thị.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: '12px 24px' }}>
        <Button onClick={onClose} startIcon={<CloseRoundedIcon />} variant="text">
          Đóng
        </Button>
        <Button onClick={handleConfirm} variant="contained" startIcon={<FactCheckOutlinedIcon />}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------
   PRINT DIALOG CHÍNH
------------------------------------------------------- */
/**
 * @param {"import"|"export"} [slipType]
 * @param {"import"|"export"} [type]       // tương thích ngược
 */
function PrintDialog({ open, onClose, orderData, slipType, type: legacyType }) {
  const printRef = useRef(null);
  const [companyId, setCompanyId] = useState(companies?.[0]?.id ?? '');
  const company = getCompany(companyId);

  // trạng thái picker dùng chung cho 3 nút
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState(null); // 'contract' | 'po' | 'kiemsoat'

  // Tự xác định loại phiếu (in)
  const resolvedType = useMemo(() => {
    const explicit = (slipType || legacyType || '').toLowerCase();
    if (explicit === 'export' || explicit === 'import') return explicit;
    if (orderData?.soNumber) return 'export';
    if (orderData?.poNumber) return 'import';
    if (Array.isArray(orderData?.items) && orderData.items.some((it) => it?.exportPrice != null))
      return 'export';
    return 'export';
  }, [slipType, legacyType, orderData]);

  const isExport = resolvedType === 'export';
  const SlipComponent = isExport ? PrintableExportSlip : PrintableImportSlip;

  // In phiếu
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${isExport ? 'Phieu-Xuat' : 'Phieu-Nhap'}-${
      orderData?.poNumber || orderData?.soNumber || ''
    }`,
    onBeforeGetContent: () => new Promise((res) => setTimeout(res, 300)),
  });

  // Mở picker theo mode
  const openPicker = (mode) => {
    if (!orderData?.items?.length) return;
    setPickerMode(mode);
    setPickerOpen(true);
  };

  // Sau khi chọn xong -> gọi exporter tương ứng (đÃ TÍNH LẠI trong util)
  const handlePickerConfirm = (selectedItems) => {
    if (!orderData) return;
    const selectedOrder = { ...orderData, items: selectedItems };

    const map = {
      contract: () => exportContract(selectedOrder, company),
      po: () => exportPODocxLikeSO(selectedOrder, company),
      kiemsoat: () => exportKiemSoatDocx(selectedOrder, company),
    };

    map[pickerMode]?.();
  };

  // Nhãn & tiêu đề cho từng mode
  const pickerTitle =
    pickerMode === 'contract'
      ? 'Chọn sản phẩm đưa vào Hợp đồng'
      : pickerMode === 'po'
      ? 'Chọn sản phẩm đưa vào ĐƠN ĐẶT HÀNG'
      : 'Chọn sản phẩm đưa vào Phiếu kiểm soát';

  const pickerConfirmLabel =
    pickerMode === 'contract'
      ? 'Xuất Hợp đồng'
      : pickerMode === 'po'
      ? 'Xuất ĐƠN ĐẶT HÀNG'
      : 'Xuất Phiếu kiểm soát';

  return (
    <>
      {/* Bản ẩn để react-to-print lấy nội dung */}
      {orderData && (
        <div style={{ display: 'none' }}>
          <SlipComponent ref={printRef} order={orderData} company={company} />
        </div>
      )}

      {/* UI dialog chính */}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          {/* Chọn công ty để render vào phiếu/Word */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Chọn công ty</InputLabel>
            <Select
              label="Chọn công ty"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {orderData ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SlipComponent order={orderData} company={company} />
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Chưa có dữ liệu phiếu để hiển thị. Hãy chọn/khởi tạo một phiếu trước.
            </Typography>
          )}
        </DialogContent>

        {/* Footer actions */}
        <DialogActions sx={{ p: '16px 24px', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={onClose} startIcon={<CloseRoundedIcon />} variant="outlined">
            Đóng
          </Button>

          {/* 3 nút xuất Word chỉ hiện khi là phiếu XUẤT */}
          {isExport && (
            <>
              <Button
                onClick={() => openPicker('contract')}
                variant="contained"
                color="warning"
                startIcon={<ArticleOutlinedIcon />}
                disabled={!orderData || !orderData.items || !orderData.items.length}
              >
                Hợp đồng
              </Button>

              <Button
                onClick={() => openPicker('po')}
                variant="contained"
                color="info"
                startIcon={<NoteAddOutlinedIcon />}
                disabled={!orderData || !orderData.items || !orderData.items.length}
              >
                ĐƠN ĐẶT HÀNG
              </Button>

              <Button
                onClick={() => openPicker('kiemsoat')}
                variant="contained"
                color="success"
                startIcon={<FactCheckOutlinedIcon />}
                disabled={!orderData || !orderData.items || !orderData.items.length}
              >
                Phiếu kiểm soát
              </Button>
            </>
          )}

          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={!orderData}
          >
            In phiếu {isExport ? 'xuất' : 'nhập'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hộp thoại chọn sản phẩm dùng chung */}
      <ProductPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        items={orderData?.items || []}
        onConfirm={handlePickerConfirm}
        title={pickerTitle}
        confirmLabel={pickerConfirmLabel}
      />
    </>
  );
}

export default PrintDialog;
